import { Card } from '@guridosdrones/ui';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  CloudLightning,
  GraduationCap,
  ListChecks,
  PlayCircle,
  Users2,
} from 'lucide-react';
import { FadeIn } from '@/components';
import { getWhatsAppLink, WHATSAPP_MESSAGES } from '@/utils/whatsapp';

const steps = [
  {
    title: 'Inscrição e Acesso',
    time: 'Imediato',
    phase: 'FASE_01',
    description:
      'Assim que sua inscrição é confirmada, você recebe os dados de acesso por e-mail e já pode começar a assistir às primeiras aulas.',
    icon: PlayCircle,
  },
  {
    title: 'Domínio Técnico',
    time: 'Semana 1',
    phase: 'FASE_02',
    description:
      'Aprenda tudo sobre a operação segura de drones DJI Agras e XAG, desde a montagem até a calibração de sensores no campo.',
    icon: CloudLightning,
  },
  {
    title: 'Mapeamento de Precisão',
    time: 'Semana 2',
    phase: 'FASE_03',
    description:
      'Domine o uso de drones de imagem para criar mapas 3D e planos de voo que economizam tempo e produto.',
    icon: ListChecks,
  },
  {
    title: 'Suporte e Comunidade',
    time: 'Vitalício',
    phase: 'FASE_04',
    description:
      'Tire suas dúvidas diretamente com o Eduardo e troque experiências com outros pilotos na nossa comunidade exclusiva.',
    icon: Users2,
  },
  {
    title: 'Certificação e Carreira',
    time: 'Finalização',
    phase: 'FASE_05',
    description:
      'Receba seu certificado digital e esteja pronto para atuar no mercado com a autoridade de quem aprendeu com o Guri dos Drones.',
    icon: GraduationCap,
  },
];

export function Timeline() {
  return (
    <section id="method" className="section-shell bg-background">
      <div className="section-container">
        <div className="section-header">
          <FadeIn direction="up" distance={20}>
            <p className="mb-3 font-mono text-xs tracking-[0.25em] text-primary/70 uppercase">
              Plano de Voo
            </p>
            <h2 className="section-title mb-4">
              Como Funciona <br className="md:hidden" />
              <span className="text-primary">a Formação</span>
            </h2>
            <p className="section-description">
              Da inscrição ao certificado em 5 etapas. Veja exatamente o que acontece em cada fase
              da sua evolução como piloto.
            </p>
          </FadeIn>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {/* Vertical Line for Desktop */}
          <div className="absolute left-[50%] top-0 bottom-0 w-px bg-linear-to-b from-transparent via-primary/20 to-transparent hidden lg:block translate-x-[-0.5px]" />

          <div className="flex flex-col gap-8 sm:gap-10 lg:gap-24">
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`flex flex-col lg:flex-row items-center gap-8 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                {/* Content */}
                <div className="flex-1 w-full lg:w-1/2">
                  <Card
                    className={`relative group overflow-hidden border-border bg-card p-6 transition-all duration-500 hover:border-primary/30 sm:p-8 ${
                      index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'
                    }`}
                  >
                    {/* Phase code watermark */}
                    <span className="absolute -top-2 -right-1 font-mono text-7xl font-black text-muted/[0.03] pointer-events-none">
                      {step.phase}
                    </span>

                    <div
                      className={`flex flex-col gap-4 ${
                        index % 2 === 0 ? 'lg:items-end' : 'lg:items-start'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-[10px] tracking-[0.2em] text-primary/50 uppercase">
                          {step.phase}
                        </span>
                        <div className="inline-flex items-center gap-2 border border-primary/20 bg-primary/8 px-3 py-1 font-mono text-[10px] font-bold tracking-widest text-primary uppercase">
                          <CheckCircle2 className="w-3 h-3" />
                          {step.time}
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold text-foreground font-heading">
                        {step.title}
                      </h3>
                      <p className="text-base leading-relaxed text-muted-foreground">
                        {step.description}
                      </p>
                    </div>
                  </Card>
                </div>

                {/* Center Node - Waypoint */}
                <div className="relative z-20 hidden shrink-0 lg:block">
                  <div className="flex h-14 w-14 items-center justify-center border border-primary/30 bg-primary/10 text-primary shadow-xl transition-all duration-500 group-hover:bg-primary/20 sm:h-16 sm:w-16">
                    <step.icon className="h-7 w-7 sm:h-8 sm:w-8" />
                  </div>
                </div>

                {/* Empty space/Balance */}
                <div className="hidden w-1/2 flex-1 lg:block" />
              </motion.div>
            ))}
          </div>
        </div>

        <FadeIn direction="up" distance={15} delay={0.15} className="mt-16 text-center sm:mt-20">
          <button
            type="button"
            onClick={() => window.open(getWhatsAppLink(WHATSAPP_MESSAGES.TIMELINE), '_blank')}
            className="group inline-flex items-center gap-3 border border-primary/40 bg-primary/10 px-7 py-3.5 font-mono text-sm font-bold tracking-widest text-primary uppercase shadow-xl shadow-primary/10 transition-all hover:border-primary/70 hover:bg-primary/20"
          >
            <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6" />
            Garantir Minha Vaga
          </button>
        </FadeIn>
      </div>
    </section>
  );
}
